import { Message } from '../lib/Localized.ts';

export enum CloudConnectorPeripheryType {
    ENTRY = 'ENTRY',
    EXIT = 'EXIT',
    PAY_STATION = 'PAY_STATION',
    TRANSIT_ENTRY = 'TRANSIT_ENTRY',
    ACCESS = 'ACCESS',
}

export enum PaperTicketType {
    PUNCH_CARD = 'PUNCH_CARD',
    QR = 'QR',
}

export enum AcquiringType {
    OPERATOR = 'OPERATOR',
    DIGITALPARKING = 'DIGITALPARKING',
}

type CloudConnectorPeripheryShared = {
    type: CloudConnectorPeripheryType;
    onPremiseId: number;
    zoneId: number;
    name: string;
    createdAt: string;
    lastSeenAt: string;
    softwareVersion: string | null;
    torizonDeviceId: string | null;
    torizonDeviceUuid: string | null;
    lastConnectedCloudConnectorId: string | null;
    externalIntercom: boolean;
};

export type EntryPeriphery = CloudConnectorPeripheryShared & {
    type: CloudConnectorPeripheryType.ENTRY;
    noTicketReader: boolean | null;
    lprCameraVersion: string | null;
    lprCameraIpAddress: string | null;
    paperTicketType: PaperTicketType | null;
};

export type ExitPeriphery = CloudConnectorPeripheryShared & {
    type: CloudConnectorPeripheryType.EXIT;
    hasEftTerminal: boolean | null;
    noTicketReader: boolean | null;
    lprCameraVersion: string | null;
    lprCameraIpAddress: string | null;
    paperTicketType: PaperTicketType | null;
    acquiringType: AcquiringType | null;
};

export type PayStationPeriphery = CloudConnectorPeripheryShared & {
    type: CloudConnectorPeripheryType.PAY_STATION;
    cashless: boolean;
    acquiringType: AcquiringType;
};

export type TransitEntryPeriphery = CloudConnectorPeripheryShared & {
    type: CloudConnectorPeripheryType.TRANSIT_ENTRY;
    withoutPresence: boolean;
    lprCameraVersion: string | null;
    lprCameraIpAddress: string | null;
};

export type AccessPeriphery = CloudConnectorPeripheryShared & {
    type: CloudConnectorPeripheryType.ACCESS;
    withoutPresence: boolean;
    lprCameraVersion: string | null;
    lprCameraIpAddress: string | null;
};

export type CloudConnectorPeriphery =
    | EntryPeriphery
    | ExitPeriphery
    | PayStationPeriphery
    | TransitEntryPeriphery
    | AccessPeriphery;

export function isEntryPeriphery(
    periphery: CloudConnectorPeriphery,
): periphery is EntryPeriphery {
    return periphery.type === CloudConnectorPeripheryType.ENTRY;
}

export function isExitPeriphery(
    periphery: CloudConnectorPeriphery,
): periphery is ExitPeriphery {
    return periphery.type === CloudConnectorPeripheryType.EXIT;
}

export function isPayStationPeriphery(
    periphery: CloudConnectorPeriphery,
): periphery is PayStationPeriphery {
    return periphery.type === CloudConnectorPeripheryType.PAY_STATION;
}

export function isTransitEntryPeriphery(
    periphery: CloudConnectorPeriphery,
): periphery is TransitEntryPeriphery {
    return periphery.type === CloudConnectorPeripheryType.TRANSIT_ENTRY;
}

export function isAccessPeriphery(
    periphery: CloudConnectorPeriphery,
): periphery is AccessPeriphery {
    return periphery.type === CloudConnectorPeripheryType.ACCESS;
}

export interface CloudConnectorPeripheryEvent {
    id: number;
    onPremiseId: number;
    peripheryType: CloudConnectorPeripheryType;
    eventType: CloudConnectorPeripheryEventType;
    category: CloudConnectorPeripheryStateEventTypeCategory;
    outOfOrderInfo?: Message;
    warnings?: Message;
}

export enum CloudConnectorPeripheryEventType {
    BARRIER_PERMANENTLY_OPEN = 'BARRIER_PERMANENTLY_OPEN',
    OUT_OF_ORDER = 'OUT_OF_ORDER',
    DISCONNECTED = 'DISCONNECTED',
    TICKET_ROLL_WARN = 'TICKET_ROLL_WARN',
    PAY_STATION_CASHBOX_FULL = 'PAY_STATION_CASHBOX_FULL',
    PAY_STATION_BILL_COLLECTOR_FULL = 'PAY_STATION_BILL_COLLECTOR_FULL',
    WARNING = 'WARNING',
    MANUAL_CLOSE = 'MANUAL_CLOSE',
    FORCED_OUT_OF_ORDER = 'FORCED_OUT_OF_ORDER',
    BARRIER_OPENS_WITHOUT_VALIDATION = 'BARRIER_OPENS_WITHOUT_VALIDATION',
}

export enum CloudConnectorPeripheryStateEventTypeCategory {
    WARNING = 'WARNING',
    ALARM = 'ALARM',
}

export function getPeripheryStatefromMainCategory(
    category: CloudConnectorPeripheryStateEventTypeCategory | null,
) {
    switch (category) {
        case CloudConnectorPeripheryStateEventTypeCategory.ALARM:
            return PeripheryStatus.ERROR;
        case CloudConnectorPeripheryStateEventTypeCategory.WARNING:
            return PeripheryStatus.ALERT;
        case null:
            return PeripheryStatus.OKAY;
    }
}

export enum PeripheryStatus {
    OKAY = 'OKAY',
    ERROR = 'ERROR',
    ALERT = 'ALERT',
}

export function getMainEventfromEvents(events: CloudConnectorPeripheryEvent[]) {
    const alarms = events.filter(
        event =>
            event.category ===
            CloudConnectorPeripheryStateEventTypeCategory.ALARM,
    );
    if (alarms.length > 0) {
        return alarms[0];
    }
    const warnings = events.filter(
        event =>
            event.category ===
            CloudConnectorPeripheryStateEventTypeCategory.WARNING,
    );
    if (warnings.length > 0) {
        return warnings[0];
    }
    return null;
}

export function getCloudConnectorEventTypeTranslation(
    event: CloudConnectorPeripheryEventType | null,
): Message {
    if (event === null) {
        return {
            de: 'OK',
            fr: 'OK',
            it: 'OK',
            en: 'OK',
        };
    }

    switch (event) {
        case CloudConnectorPeripheryEventType.BARRIER_PERMANENTLY_OPEN:
            return {
                de: 'Dauerhaft geöffnet',
                fr: 'Ouverte en permanence',
                it: 'Sempre aperta',
                en: 'Permanently open',
            };
        case CloudConnectorPeripheryEventType.OUT_OF_ORDER:
            return {
                de: 'Ausser Betrieb',
                fr: 'Hors service',
                it: 'Fuori servizio',
                en: 'Out of order',
            };
        case CloudConnectorPeripheryEventType.DISCONNECTED:
            return {
                de: 'Getrennt',
                fr: 'Déconnecté',
                it: 'Disconnesso',
                en: 'Disconnected',
            };
        case CloudConnectorPeripheryEventType.TICKET_ROLL_WARN:
            return {
                de: 'Ticketniveau tief',
                fr: 'Niveau de tickets bas',
                it: 'Livello tickets basso',
                en: 'Low tickets level',
            };
        case CloudConnectorPeripheryEventType.PAY_STATION_CASHBOX_FULL:
            return {
                de: 'Münztresor fast voll',
                fr: 'Boîte monnaie presque pleine',
                it: 'Cassetta monete quasi piena',
                en: 'Coin box almost full',
            };
        case CloudConnectorPeripheryEventType.PAY_STATION_BILL_COLLECTOR_FULL:
            return {
                de: 'Notentresor fast voll',
                fr: 'Boîte à billets presque pleine',
                it: 'Cassetta banconote quasi piena',
                en: 'Bill collector almost full',
            };
        case CloudConnectorPeripheryEventType.WARNING:
            return {
                de: 'Überprüfung erforderlich',
                fr: 'Vérification requise',
                it: 'Necessita verifica',
                en: 'Needs attention',
            };
        case CloudConnectorPeripheryEventType.MANUAL_CLOSE:
        case CloudConnectorPeripheryEventType.FORCED_OUT_OF_ORDER:
            return {
                de: 'Manuell geschlossen',
                fr: 'Fermée manuellement',
                it: 'Chiusa manualmente',
                en: 'Manually closed',
            };
        case CloudConnectorPeripheryEventType.BARRIER_OPENS_WITHOUT_VALIDATION:
            return {
                de: 'Validierung deaktiviert',
                fr: 'Validation désactivée',
                it: 'Convalida disattivata',
                en: 'Validation disabled',
            };
    }
}

export enum SpecialVehicleType {
    HEIGHT = 'HEIGHT',
    MOTORCYCLE = 'MOTORCYCLE',
}

export function getCloudConnectorPeripheryTypeTranslation(
    peripheryType: CloudConnectorPeripheryType,
): Message {
    switch (peripheryType) {
        case CloudConnectorPeripheryType.ENTRY:
            return {
                de: 'Einfahrt',
                fr: 'Entrée',
                it: 'Entrata',
                en: 'Entry',
            };
        case CloudConnectorPeripheryType.EXIT:
            return {
                de: 'Ausfahrt',
                fr: 'Sortie',
                it: 'Uscita',
                en: 'Exit',
            };

        case CloudConnectorPeripheryType.PAY_STATION:
            return {
                de: 'Kasse',
                fr: 'Caisse',
                it: 'Cassa',
                en: 'Pay station',
            };

        case CloudConnectorPeripheryType.TRANSIT_ENTRY:
            return {
                de: 'Durchfahrt',
                fr: 'Transit',
                it: 'Transito',
                en: 'Transit',
            };

        case CloudConnectorPeripheryType.ACCESS:
            return {
                de: 'Zutritt',
                fr: 'Accès',
                it: 'Accesso',
                en: 'Access',
            };
    }
}
